import { useMutation, useQueryClient } from '@tanstack/react-query'

import { createApiCall } from 'services'
import type { CertificateAdmin } from 'types'

type LinkCertificatesToGroupParams = {
  /** UUID of the EXISTING group's tagless parent — the group being added to. */
  certificateId: string
  /** Already-existing, standalone certificate ids to add as children. Must be >= 1. */
  childIds: string[]
}

type LinkCertificatesToGroupResult = {
  parent: CertificateAdmin
  children: CertificateAdmin[]
}

type UseLinkCertificatesToGroupOptions = {
  onSuccess?: (data: LinkCertificatesToGroupResult) => void
  onError?: (error?: unknown) => void
}

// VRG-231 (add-to-existing-group) — the counterpart to
// useCreateGroupFromExisting.ts for a selection that ALREADY includes an
// existing group's parent (CertificatesPage.handleOpenCreateGroupModalBulk):
// adds the other selected certificates to that group instead of creating a
// second, unrelated one. Calls POST /certificates/admin/groups/:certificateId/link,
// which inherits the group's divisibility server-side — this payload
// deliberately carries no `divisibility` field, matching
// LinkCertificatesToGroupPayloadDto on the API.
export const useLinkCertificatesToGroup = (options?: UseLinkCertificatesToGroupOptions) => {
  const queryClient = useQueryClient()

  const linkMutation = useMutation({
    mutationFn: async (params: LinkCertificatesToGroupParams) => {
      const { certificateId, childIds } = params
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/admin/groups/${certificateId}/link`,
        data: { childIds },
      })
      return response.data as LinkCertificatesToGroupResult
    },
    onSuccess: data => {
      // Same reasoning as useCreateGroupFromExisting: the parent and every
      // linked member all change status — invalidate the whole certificates
      // list/detail tree rather than enumerating ids.
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
      options?.onSuccess?.(data)
    },
    onError: error => {
      options?.onError?.(error)
    },
  })

  return {
    linkCertificatesToGroup: (params: LinkCertificatesToGroupParams) => linkMutation.mutateAsync(params),
    isLinkingCertificates: linkMutation.isPending,
  }
}
